// The single editable working-state for one material receipt.
//
// The matching card is read-only and renders from this draft; the ReceiptDrawer
// is the only surface that edits it. On Save / Confirm / exception, the draft is
// flushed to the backend (PATCH receipt fields; confirm creates the job match).

import type { JobCategory } from './docTypes';
import { isRegulatory } from './docTypes';
import type { MaterialReceiptRow, MaterialReceiptPatch, MatchJobInput, NoteEntry } from '../types/api';

// "YYYY-MM-DD HH:MM" stamp for a new note entry.
export const noteStamp = (): string => new Date().toISOString().slice(0, 16).replace('T', ' ');

// The exception headline: the LATEST 'reason' entry in the note log (replaces
// the old single exception_reason column).
export function latestReason(log: NoteEntry[] | null | undefined): NoteEntry | null {
  if (!log) return null;
  for (let i = log.length - 1; i >= 0; i--) if (log[i]?.kind === 'reason') return log[i];
  return null;
}

// The latest human note (operator commentary / escalation handoff) — anything
// that isn't a reason headline or a machine-generated system entry. Legacy
// entries without a kind count as notes.
export function latestHandoffNote(log: NoteEntry[] | null | undefined): NoteEntry | null {
  if (!log) return null;
  for (let i = log.length - 1; i >= 0; i--) {
    const k = log[i]?.kind;
    if (log[i] && k !== 'reason' && k !== 'system') return log[i];
  }
  return null;
}

export interface DraftJob {
  jobNumber: string;
  issuerName: string;
  jobType: string;       // internal job-type code (Y/P/I) — NOT the category
  jobStatus: string;
  jobIsNaa: boolean | null;
  ordered: Record<string, number>;  // docType → ordered qty
}

export const MAX_JOBS = 3;

export interface DraftDoc { type: string; qty: number; }

export interface ReceiptDraft {
  receiptId: string;
  category: JobCategory;
  scan: string;
  intakeDate: string;
  fileUri?: string;
  filename: string;
  // Scanned / received side (editable)
  clientNames: string[];
  eventDate: string;
  recordDate: string;
  effectiveDate: string;
  receivedDocs: DraftDoc[];
  // Logistics
  location: string;
  distributor: string;
  carrier: string;
  intakeBy: string;
  hasPkSlip: boolean | null;
  pkEnvelope: string;
  pkPackages: string;
  pkBoxes: string;
  pkSkids: string;
  mailEnvelopeSize: string;
  sampleWeight: string;
  // Regulatory-only
  bookName: string;
  addressed: string;
  litCode: string;
  tracking: string;
  ffcOnly: string;
  errorCode: string;
  materialType: string;
  cusip: string;
  ticker: string;
  postSale: boolean;
  quantity: string;
  noteLog: NoteEntry[];
  newNote: string;
  jobs: DraftJob[];
}

const numOr = (v: any, d = '') => (v == null ? d : String(v));

export function draftFromRow(row: MaterialReceiptRow): ReceiptDraft {
  const category = (row.job_category || 'P') as JobCategory;
  const receivedDocs: DraftDoc[] = Object.entries(row.received_docs || {}).map(
    ([type, qty]) => ({ type, qty: Number(qty) || 0 }),
  );
  if (receivedDocs.length === 0) receivedDocs.push({ type: '', qty: 0 });

  // Seed jobs from any existing matched jobs; else one empty job for entry.
  const jobs: DraftJob[] = (row.jobs && row.jobs.length ? row.jobs : [null]).map((j: any) => ({
    jobNumber: j?.job_number ?? '',
    issuerName: '',
    jobType: j?.job_type_code ?? '',   // internal code (Y/P/I) — round-trips from the server
    jobStatus: j?.job_status ?? '',
    jobIsNaa: j?.job_is_naa ?? null,
    ordered: {},
  }));

  return {
    receiptId: row.id,
    category,
    scan: row.scan_number,
    intakeDate: row.intake_date || '',
    fileUri: row.file_uri,
    filename: `${row.scan_number}.pdf`,
    clientNames: row.client_names || [],
    eventDate: row.event_date || '',
    recordDate: row.record_date || '',
    effectiveDate: row.effective_date || '',
    receivedDocs,
    location: row.location || '',
    distributor: row.distributor || '',
    carrier: row.carrier || '',
    intakeBy: row.intake_by || '',
    hasPkSlip: row.has_pk_slip,
    pkEnvelope: numOr(row.pk_envelope_count),
    pkPackages: numOr(row.pk_packages_count),
    pkBoxes: numOr(row.pk_boxes_count),
    pkSkids: numOr(row.pk_skids_count),
    mailEnvelopeSize: row.mail_envelope_size || '',
    sampleWeight: row.sample_weight || '',
    bookName: row.book_name || '',
    addressed: row.addressed || '',
    litCode: row.lit_code || '',
    tracking: row.tracking || '',
    ffcOnly: row.ffc_only || '',
    errorCode: row.error_code || '',
    // Frontend-only regulatory fields — no backend column yet (like DraftJob.issuerName below).
    materialType: '',
    cusip: '',
    ticker: '',
    postSale: false,
    quantity: '',
    noteLog: row.note_log || [],
    newNote: '',
    jobs,
  };
}

const toInt = (s: string) => (s === '' || s == null ? null : Math.max(0, parseInt(s, 10) || 0));

// The receipt-column subset of a draft, for PATCH /material-receipts/{id}.
export function buildReceiptPatch(d: ReceiptDraft): MaterialReceiptPatch {
  const reg = isRegulatory(d.category);
  const docs: Record<string, number | null> = {};
  d.receivedDocs.forEach(r => { if (r.type) docs[r.type] = r.qty; });
  const patch: MaterialReceiptPatch = {
    client_names: d.clientNames,
    received_docs: docs,
    location: d.location || undefined,
    distributor: d.distributor || undefined,
    carrier: d.carrier || undefined,
    intake_by: d.intakeBy || undefined,
    has_pk_slip: d.hasPkSlip ?? undefined,
    note_log: d.noteLog,
  };
  const env = toInt(d.pkEnvelope); if (env != null) patch.pk_envelope_count = env;
  const pkg = toInt(d.pkPackages); if (pkg != null) patch.pk_packages_count = pkg;
  const box = toInt(d.pkBoxes); if (box != null) patch.pk_boxes_count = box;
  const skid = toInt(d.pkSkids); if (skid != null) patch.pk_skids_count = skid;
  if (d.mailEnvelopeSize) patch.mail_envelope_size = d.mailEnvelopeSize;
  if (d.sampleWeight) patch.sample_weight = d.sampleWeight;
  if (reg) {
    patch.effective_date = d.effectiveDate || null;
    patch.book_name = d.bookName || undefined;
    patch.addressed = d.addressed || undefined;
    patch.lit_code = d.litCode || undefined;
    patch.tracking = d.tracking || undefined;
    patch.ffc_only = d.ffcOnly || undefined;
    patch.error_code = d.errorCode || undefined;
  } else {
    patch.event_date = d.eventDate || null;
    patch.record_date = d.recordDate || null;
  }
  return patch;
}

// The job-match payload for POST /material-receipts/{id}/match.
export function draftToMatchJobs(d: ReceiptDraft): MatchJobInput[] {
  return d.jobs
    .filter(j => (j.jobNumber || '').trim())
    .map(j => ({
      job_number: j.jobNumber.trim(),
      job_status: j.jobStatus || null,
      job_category: d.category,        // fixed receipt category (P/CA/R/I)
      job_type_code: j.jobType || null, // internal code (Y/P/I)
      job_is_naa: j.jobIsNaa,
    }));
}

// 2-line package summary posted to chat when a Proxy receipt is matched.
export function buildProxyReceivedLines(d: ReceiptDraft): string {
  let type = '';
  if (toInt(d.pkEnvelope)) type = 'ENV';
  else if (toInt(d.pkPackages)) type = 'PKG';
  else if (toInt(d.pkBoxes)) type = 'BOX';
  else if (toInt(d.pkSkids)) type = 'SKID';

  const intakeMDY = (() => {
    const [y, m, day] = (d.intakeDate || '').split('-');
    return y && m && day ? `${m}/${day}/${y}` : d.intakeDate;
  })();
  const line1 = [type, 'REC', intakeMDY, d.mailEnvelopeSize, d.sampleWeight, d.scan, d.location]
    .map(v => (v ?? '').toString().trim())
    .join(' ')
    .replace(/\s+/g, ' ')   // collapse gaps left by empty fields
    .trim();
  return `${line1}\n[USPS Std Mail]`;
}

export type Disposition = 'matched' | 'received_short' | 'no_job_found' | 'route_to_cs';

export function orderedFor(d: ReceiptDraft, type: string): number {
  return d.jobs.reduce((s, j) => s + (Number(j.ordered[type]) || 0), 0);
}

// Which doc types came up short and by how much, for the Received Short note.
export function shortageSummary(d: ReceiptDraft): string {
  return d.receivedDocs
    .map(r => ({ type: r.type || '—', received: r.qty, ordered: orderedFor(d, r.type) }))
    .filter(s => s.ordered > s.received)
    .map(s => `${s.type} ${s.received} of ${s.ordered} (short ${s.ordered - s.received})`)
    .join(', ');
}

// Which doc types came in over ordered and by how much, for the Overs note.
export function oversSummary(d: ReceiptDraft): string {
  return d.receivedDocs
    .map(r => ({ type: r.type || '—', received: r.qty, ordered: orderedFor(d, r.type) }))
    .filter(s => s.received > s.ordered)
    .map(s => `${s.type} ${s.received} of ${s.ordered} (over ${s.received - s.ordered})`)
    .join(', ');
}

// Auto-detected routing the read-only card surfaces.
export function computeRouting(d: ReceiptDraft): { disposition: Disposition; text: string; cls: string } {
  const hasJob = d.jobs.some(j => (j.jobNumber || '').trim());
  const qtyMismatch = d.receivedDocs.some(r => r.qty !== orderedFor(d, r.type));
  const docMismatch = d.receivedDocs.some(r => !r.type)
    || d.receivedDocs.some(r => (r.qty > 0) !== (orderedFor(d, r.type) > 0));
  if (!hasJob) return { disposition: 'no_job_found', text: 'No job number entered — likely No Job Found.', cls: 'bg-red-50 border-red-200 text-red-700' };
  if (qtyMismatch || docMismatch) return { disposition: 'received_short', text: docMismatch ? 'Doc types don’t line up — likely Received Short.' : 'Quantities don’t match — likely Received Short.', cls: 'bg-amber-50 border-amber-200 text-amber-800' };
  return { disposition: 'matched', text: 'Conditions met — ready for the Receiving Log.', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
}

// Auto-detected routing for Regulatory receipts — same shape as computeRouting,
// but job#/qty/doc rules specific to the Regulatory Cell (No Job # found,
// Qty ≥ 200 routes to CS, doc-type mismatch reads as Received Short).
export function computeRegulatoryRouting(d: ReceiptDraft): { disposition: Disposition; text: string; cls: string } {
  const hasJob = d.jobs.some(j => (j.jobNumber || '').trim());
  const qty = Number(d.quantity) || 0;
  const qtyMismatch = d.receivedDocs.some(r => r.qty !== orderedFor(d, r.type));
  const docMismatch = d.receivedDocs.some(r => !r.type)
    || d.receivedDocs.some(r => (r.qty > 0) !== (orderedFor(d, r.type) > 0));
  if (!hasJob) return { disposition: 'no_job_found', text: 'No Job # found — search reports.', cls: 'bg-red-50 border-red-200 text-red-700' };
  if (qty >= 200) return { disposition: 'route_to_cs', text: 'Qty is 200 or more, so route to CS.', cls: 'bg-amber-50 border-amber-200 text-amber-800' };
  if (qtyMismatch || docMismatch) return { disposition: 'received_short', text: 'Doc types don’t line up — likely Received Short.', cls: 'bg-amber-50 border-amber-200 text-amber-800' };
  return { disposition: 'matched', text: 'Conditions met — ready for the Receiving Log.', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
}
